// api/v1/client.ts
import axios, {
  type AxiosError,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';

import Cookies from 'js-cookie';

import { isArray, isString } from 'utils/check';
import { addNotification } from 'notifications';
import {
  attachCsrfHeader,
  ensureCsrfToken,
  getCsrfToken,
  readCsrfFromHeaders,
  rememberCsrfFromResponse,
  shouldAttachCsrf,
  CSRF_COOKIE_KEY,
  CSRF_HEADER,
} from 'api/csrf';

const readCsrfCookie = (): string | undefined => Cookies.get(CSRF_COOKIE_KEY) || undefined;

export type ApiRequestConfig<T = any> = AxiosRequestConfig<T> & {
  skipGlobalErrorHandler?: boolean;
  skipAuthRefresh?: boolean;
  skipCsrf?: boolean;
  skipAuthHeader?: boolean;
  _csrfRetried?: boolean;
};

const isDevelopment = import.meta.env.DEV;
const isLocalhost =
  typeof window !== 'undefined' && window.location.hostname === 'localhost';
const isAppSubdomain =
  typeof window !== 'undefined' &&
  /^(profile|admin)\.(dev\.)?myteacher\.pro$/.test(window.location.hostname);

let baseURL: string;
let wsBaseURL: string;

if (isLocalhost && isDevelopment) {
  baseURL = '/v1';
  wsBaseURL = '/ws';
} else if (isAppSubdomain) {
  baseURL = '/v1';
  wsBaseURL = '/ws';
} else {
  baseURL = '/v1';
  wsBaseURL = '/ws';
}

const api = axios.create({
  baseURL,
  withCredentials: true,
});

/** Вход, регистрация и проверка сессии не должны крутить спиннер без конца. */
const AUTH_REQUEST_TIMEOUT_MS = 12_000;
const AUTH_TIMEOUT_PATH = /\/(login\/|register\/|auth\/|otp\/)/;

api.interceptors.request.use((config) => {
  // У axios timeout по умолчанию 0 (ждать бесконечно), это не null.
  if (!config.timeout && AUTH_TIMEOUT_PATH.test(config.url || '')) {
    config.timeout = AUTH_REQUEST_TIMEOUT_MS;
  }
  return config;
});

const wsApi = axios.create({
  baseURL: wsBaseURL,
  withCredentials: true,
});

const csrfInterceptor = async (config: InternalAxiosRequestConfig) => {
  const cfg = config as InternalAxiosRequestConfig & ApiRequestConfig;
  if (cfg.skipCsrf || !shouldAttachCsrf(config.method)) {
    return config;
  }

  // Повтор после 403: заголовок уже выставлен свежим токеном из /auth/csrf/, не перетираем.
  if (cfg._csrfRetried && config.headers?.get?.(CSRF_HEADER)) {
    return config;
  }

  // Сервер сверяет заголовок с токеном текущей сессии. Источник правды - ответ /auth/csrf/
  // и заголовок X-CSRF-Token ответов (это токен сессии). Cookie csrf_token берем только как
  // запасной вариант: в браузере может лежать несколько cookie с этим именем (host-only,
  // Domain=.dev.myteacher.pro, Domain=.myteacher.pro от прода), и JS видит первую - не обязательно нашу.
  let token = getCsrfToken() || readCsrfCookie();
  if (!token) {
    await ensureCsrfToken();
    token = getCsrfToken() || readCsrfCookie();
  }
  if (token) {
    config.headers = attachCsrfHeader(config.headers, token);
  }
  return config;
};

api.interceptors.request.use(csrfInterceptor);
wsApi.interceptors.request.use(csrfInterceptor);

api.interceptors.response.use((response) => {
  rememberCsrfFromResponse(readCsrfFromHeaders(response.headers));
  return response;
});

wsApi.interceptors.response.use((response) => {
  rememberCsrfFromResponse(readCsrfFromHeaders(response.headers));
  return response;
});

function showGlobalError(error: AxiosError) {
  const cfg = error.config as ApiRequestConfig | undefined;
  if (cfg?.skipGlobalErrorHandler) {
    return;
  }

  const message = (error.response?.data as { message?: string })?.message;
  const detail = (error.response?.data as { detail?: unknown })?.detail;

  if (message) {
    addNotification(message, 'error');
  } else if (isString(detail)) {
    // Убираем из текста API служебные подсказки для разработчиков («… POST /v1/…»).
    const humanDetail = detail
      .replace(/[^.!?]*(?:GET|POST|PUT|PATCH|DELETE)\s+\/v1\/[^.!?]*[.!?]?/g, '')
      .trim();
    addNotification(humanDetail || 'Что-то пошло не так', 'error');
  } else if (isArray(detail)) {
    detail.forEach((d) => {
      const msg = (d as { msg?: string })?.msg;
      if (msg) addNotification(msg, 'error');
    });
  } else {
    addNotification('Что-то пошло не так', 'error', { preventDuplicate: true });
  }
}

const CSRF_HUMAN_MESSAGE =
  'Не удалось подтвердить вход: сессия браузера устарела. Обновите страницу (F5) и попробуйте еще раз. Если не поможет - удалите cookie сайта myteacher.pro.';

function isCsrfError(error: AxiosError): boolean {
  const detail = (error.response?.data as { detail?: unknown })?.detail;
  return (
    error.response?.status === 403 &&
    (isString(detail) ? detail.toLowerCase().includes('csrf') : false)
  );
}

const errorInterceptor = async (error: AxiosError) => {
  const status = error.response?.status;
  const cfg = error.config as ApiRequestConfig | undefined;

  if (
    axios.isAxiosError(error) &&
    isCsrfError(error) &&
    cfg &&
    !cfg._csrfRetried &&
    !cfg.skipCsrf
  ) {
    cfg._csrfRetried = true;
    try {
      const token = await ensureCsrfToken(true);
      if (token) {
        cfg.headers = attachCsrfHeader(cfg.headers as any, token);
      }
      return await api.request(cfg);
    } catch (retryError) {
      return Promise.reject(retryError);
    }
  }

  // После повтора CSRF всё ещё не сошёлся: вместо «Неверный CSRF-токен» - понятный текст.
  if (axios.isAxiosError(error) && isCsrfError(error) && error.response) {
    const data = (error.response.data ?? {}) as Record<string, unknown>;
    error.response.data = {
      ...data,
      code: 'csrf_failed',
      technical_detail: data.detail,
      detail: CSRF_HUMAN_MESSAGE,
    };
  }

  if (axios.isAxiosError(error) && status !== 401 && !cfg?.skipGlobalErrorHandler) {
    showGlobalError(error);
  } else if (!axios.isAxiosError(error)) {
    addNotification('Нет соединения', 'error');
  }

  return Promise.reject(error);
};

api.interceptors.response.use((response) => response, errorInterceptor);
wsApi.interceptors.response.use((response) => response, errorInterceptor);

export { api, wsApi };
